import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import cookie from 'js-cookie';
import queryString from 'query-string';

import { MirrorService } from 'services/MirrorService';

import { getIsDisabledPerformanceCookies } from 'helpers/cookiesSettings';
import { assertFunnel, assertIntroSkipFunnel } from 'helpers/funnels/asserts';
import { Funnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { getUserIdFromCookies } from 'helpers/userId';
import { useSystemSearchQueries } from 'hooks/useSystemSearchQueries';

const INTRO_SKIP_FUNNEL_COOKIE_NAME = 'INTRO_SKIP_FUNNEL';

interface Props {
  isPrivate?: boolean;
}

export const useRestrictedRedirect = ({ isPrivate }: Props) => {
  const location = useLocation();

  const {
    getPartnerIdQuery,
    getPartnerLoginIdQuery,
    getApiDynQuery,
    getApiRegQuery,
    getReqCidQuery,
    getBackButtonQuery,
    getEventDataIdQuery,
    getClickIdQuery,
    getConfirmMailQuery,
  } = useSystemSearchQueries();

  const { value: partnerId } = getPartnerIdQuery();
  const { value: partnerLoginId } = getPartnerLoginIdQuery();
  const { value: apiDyn } = getApiDynQuery();
  const { value: apiReg } = getApiRegQuery();
  const { value: reqCid } = getReqCidQuery();
  const { value: backButton } = getBackButtonQuery();
  const { value: clickId } = getClickIdQuery();
  const { value: eventDataId } = getEventDataIdQuery();
  const { value: confirmMail } = getConfirmMailQuery();

  const userId = getUserIdFromCookies();
  const introSkipFunnelCookie = cookie.get(INTRO_SKIP_FUNNEL_COOKIE_NAME);
  const introSkipFunnelSessionStorage = getSessionStorageItem(
    INTRO_SKIP_FUNNEL_COOKIE_NAME
  );

  const isIntroSkipFunnelSplitActive = useMemo(
    () =>
      !!userId &&
      !introSkipFunnelCookie &&
      !introSkipFunnelSessionStorage &&
      partnerId &&
      !partnerLoginId &&
      !confirmMail &&
      (MirrorService.isHookupers ||
        MirrorService.isLustStation ||
        MirrorService.isLocalsJoy),
    [
      confirmMail,
      introSkipFunnelCookie,
      introSkipFunnelSessionStorage,
      partnerId,
      partnerLoginId,
      userId,
    ]
  );

  const isApiBackButtonSplitActive = useMemo(
    () =>
      !!userId &&
      MirrorService.isFirstSiteGroup &&
      (apiDyn === '1' || apiReg === '1'),
    [apiDyn, apiReg, userId]
  );

  const redirectPathname = useMemo(() => {
    if (isIntroSkipFunnelSplitActive) {
      return `/upgrade`;
    }
    if (isPrivate) {
      return '/';
    }
    return '/finder';
  }, [isIntroSkipFunnelSplitActive, isPrivate]);

  const redirectSearch = useMemo(() => {
    const search = [];

    if (isIntroSkipFunnelSplitActive && redirectPathname.includes('/upgrade')) {
      search.push(
        getFunnelSearchString(Funnels.IntroSkip),
        `event_data_id=${eventDataId}`,
        `click_id=${clickId}`
      );
    }

    if (isApiBackButtonSplitActive) {
      search.push(
        'bb=2',
        `partner_id=${partnerId}`,
        `req_cid=${reqCid}`,
        `event_data_id=${eventDataId}`,
        `click_id=${clickId}`
      );
    }

    return search.length ? `?${search.join('&')}` : '';
  }, [
    clickId,
    eventDataId,
    isApiBackButtonSplitActive,
    isIntroSkipFunnelSplitActive,
    partnerId,
    redirectPathname,
    reqCid,
  ]);

  useEffect(() => {
    const searchParams = queryString.parse(location.search);
    const maybeFunnel = assertFunnel(searchParams.funnel)
      ? searchParams.funnel
      : undefined;
    const isIntroSkipFunnel = maybeFunnel && assertIntroSkipFunnel(maybeFunnel);

    if (isIntroSkipFunnel) {
      if (!getIsDisabledPerformanceCookies()) {
        cookie.set(INTRO_SKIP_FUNNEL_COOKIE_NAME, '1', {
          domain: window.location.hostname,
          expires: 3, // days (72 hours)
        });
      }

      setSessionStorageItem(INTRO_SKIP_FUNNEL_COOKIE_NAME, '1');
    }
  }, [location]);

  return {
    redirectSearch,
    redirectPathname,
    isIntroSkipFunnelSplitActive,
    isApiBackButtonRedirected: backButton === '2', // prevent second redirect after bb redirect
  };
};
